import { useGlobalQuery } from '@/state/query-client/hooks';
import { useQueueCounts } from '@/hooks/use-queue-counts';
import { createApplicationHooks } from '@/modules/service-requests/shared/application-hooks';
import {
  deleteNocApplication,
  generateNocLetter,
  getNocTypes,
  nocApi,
  reviewNocApplication,
  submitNocApplication,
} from '../services/noc.service';
import { NocApplication } from '../types/noc.types';
import { buildNocFilter } from '../utils/noc-filter';
import { EMPTY_REQUEST_FILTERS } from '../../shared/service-desk.types';
import { useDepartmentScope } from '@/modules/shared/hr-lookups';

const hooks = createApplicationHooks<NocApplication>({
  key: 'noc',
  api: nocApi,
  withdrawWorkflow: 'NOC_APPLICATION_WITHDRAW',
});

export const useNocApplications = hooks.useApplications;
export const useNocApplication = hooks.useApplication;
export const useWithdrawNoc = hooks.useWithdrawApplication;

export const useSubmitNoc = () =>
  hooks.useApplicationMutation(submitNocApplication, 'REQUEST_SUBMITTED');
export const useReviewNoc = () =>
  hooks.useApplicationMutation(reviewNocApplication, 'REQUEST_UPDATED');
export const useGenerateNoc = () =>
  hooks.useApplicationMutation(generateNocLetter, 'NOC_GENERATED');
export const useDeleteNoc = () =>
  hooks.useApplicationMutation(deleteNocApplication, 'REQUEST_DELETED');

/**
 * The queues of the NOC requests list (Component Map › Queue status, as the prototype's Team Requests):
 * Approved holds both accepted applications and those whose letter is generated.
 */
export const NOC_QUEUES = ['Pending', 'Approved', 'Rejected', 'All'] as const;
export type NocQueue = (typeof NOC_QUEUES)[number];
export const NOC_QUEUE_LABEL: Record<NocQueue, string> = {
  Pending: 'PENDING',
  Approved: 'APPROVED',
  Rejected: 'REJECTED',
  All: 'ALL',
};
export const nocQueueFilter = (queue: NocQueue): Record<string, unknown> => {
  if (queue === 'All') return {};
  if (queue === 'Approved') return { Status: { $in: ['Accepted', 'Generated'] } };
  return { Status: queue };
};

/** How many NOC applications each queue holds within `base` (the reviewer's scope and filters). */
export const useNocQueueCounts = (base: Record<string, unknown>, enabled = true) =>
  useQueueCounts({
    // Under the NOC hooks' root key, so reviewing or deleting refreshes the counts too.
    queryKey: ['hr-service-desk', 'noc'],
    base,
    queues: NOC_QUEUES.map((value) => ({ value, filter: nocQueueFilter(value) })),
    fetchTotal: async (filter) =>
      (await nocApi.list({ pageNo: 1, pageSize: 1, filter })).totalCount,
    enabled,
  });

export const useNocTypes = () =>
  useGlobalQuery({
    queryKey: ['hr-service-desk', 'noc', 'types'],
    queryFn: getNocTypes,
    staleTime: 30 * 60 * 1000,
  });

/** NOCs waiting for the user's approval in the departments they head (Team Requests' and Home's count). */
export const useTeamPendingNocCount = (enabled: boolean) => {
  const { getDepartmentIdCondition, isLoading } = useDepartmentScope({ teamOnly: true });
  const base = buildNocFilter(
    EMPTY_REQUEST_FILTERS,
    { from: '', to: '' },
    getDepartmentIdCondition(EMPTY_REQUEST_FILTERS.departmentId)
  );
  return useNocQueueCounts(base, enabled && !isLoading).Pending;
};